import { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { EUROPEAN_NAMES, INVESTMENT_AMOUNTS, PROFIT_LABELS } from '@/mocks/users';

interface Notification {
  id: number;
  name: string;
  amount: number;
  profitLabel: string;
  timeLabelKey: string;
}

let notifId = 0;
const TIME_KEYS = ['activity.just_now', 'activity.1min', 'activity.2min', 'activity.3min'];

const generateNotif = (): Notification => {
  const idx = Math.floor(Math.random() * INVESTMENT_AMOUNTS.length);
  return {
    id: notifId++,
    name: EUROPEAN_NAMES[Math.floor(Math.random() * EUROPEAN_NAMES.length)],
    amount: INVESTMENT_AMOUNTS[idx],
    profitLabel: PROFIT_LABELS[idx],
    timeLabelKey: TIME_KEYS[0],
  };
};

const initNotifs = (): Notification[] =>
  Array.from({ length: 4 }, () => {
    const n = generateNotif();
    n.timeLabelKey = TIME_KEYS[Math.floor(Math.random() * TIME_KEYS.length)];
    return n;
  });

const AvatarBadge = ({ name }: { name: string }) => {
  const initials = name.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase();
  const colors = ['#16a34a', '#15803d', '#166534', '#22c55e', '#4ade80'];
  const color = colors[name.charCodeAt(0) % colors.length];
  return (
    <div className="w-9 h-9 rounded-full flex items-center justify-center text-white font-bold text-xs flex-shrink-0"
      style={{ backgroundColor: color }}>
      {initials}
    </div>
  );
};

const ActivityFeed = () => {
  const { t } = useTranslation();
  const [notifs, setNotifs] = useState<Notification[]>(initNotifs);
  const [newId, setNewId] = useState<number | null>(null);
  const timerRef = useRef<number>(0);

  useEffect(() => {
    timerRef.current = window.setInterval(() => {
      const n = generateNotif();
      setNewId(n.id);
      setNotifs(prev => [n, ...prev].slice(0, 6));
      setTimeout(() => setNewId(null), 600);
    }, 5000);
    return () => clearInterval(timerRef.current);
  }, []);

  return (
    <section id="activity" className="py-20 md:py-28" style={{ background: 'linear-gradient(180deg, #0d1117 0%, #0a1a0e 100%)' }}>
      <div className="max-w-7xl mx-auto px-4 md:px-8">
        <div className="flex flex-col lg:flex-row gap-12 items-start">
          {/* Left: activity panel */}
          <div className="w-full lg:w-80 flex-shrink-0">
            <div className="bg-[#141a14] border border-green-900/40 rounded-2xl overflow-hidden">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-green-900/30">
                <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></span>
                <span className="text-white font-semibold text-sm">{t('activity.header')}</span>
              </div>
              <div className="p-3 space-y-2" style={{ minHeight: 320 }}>
                {notifs.map(n => (
                  <div key={n.id}
                    className="relative flex items-center gap-3 bg-[#1a2a1a] border border-green-900/30 rounded-xl p-3 transition-all duration-300"
                    style={{
                      boxShadow: newId === n.id ? '0 0 16px 2px rgba(34,197,94,0.35)' : 'none',
                      borderColor: newId === n.id ? 'rgba(34,197,94,0.5)' : 'rgba(34,100,50,0.3)',
                      animation: newId === n.id ? 'slideInLeft 0.4s ease' : 'none',
                    }}>
                    <div className="absolute left-0 top-3 bottom-3 w-0.5 bg-green-500 rounded-full"></div>
                    <AvatarBadge name={n.name} />
                    <div className="flex-1 min-w-0">
                      <div className="text-white font-semibold text-xs truncate">{n.name}</div>
                      <div className="text-white/50 text-xs leading-snug mt-0.5">
                        {t('activity.invested')} <span className="text-green-400 font-bold">€{n.amount.toLocaleString()}</span>
                        {' '}{t('activity.profit')}{' '}
                        <span className="text-green-400 font-bold">{n.profitLabel}</span>
                      </div>
                      <div className="text-white/30 text-xs mt-0.5">{t(n.timeLabelKey)}</div>
                    </div>
                    <i className="ri-arrow-up-line text-green-400 text-sm flex-shrink-0"></i>
                  </div>
                ))}
              </div>
              <div className="px-4 py-3 border-t border-green-900/30">
                <p className="text-white/25 text-xs leading-relaxed">{t('activity.disclaimer')}</p>
              </div>
            </div>
          </div>

          {/* Right: text block */}
          <div className="flex-1 w-full">
            <div className="inline-flex items-center gap-2 bg-green-500/10 border border-green-500/20 rounded-full px-4 py-1.5 mb-4">
              <i className="ri-group-line text-green-400 text-sm"></i>
              <span className="text-green-400 text-xs font-semibold uppercase tracking-wide">{t('activity.badge')}</span>
            </div>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-black text-white mb-6">
              {t('activity.title_p1')}<br />
              <span className="text-green-400">{t('activity.title_hl')}</span>
            </h2>
            <p className="text-white/60 text-base md:text-lg leading-relaxed mb-8 max-w-lg">{t('activity.desc')}</p>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 mb-8">
              {[
                { val: '12 400+', labelKey: 'activity.stat1' },
                { val: '€28M+',   labelKey: 'activity.stat2' },
                { val: '97%',     labelKey: 'activity.stat3' },
              ].map(s => (
                <div key={s.val} className="bg-white/5 border border-white/10 rounded-xl p-4">
                  <div className="text-green-400 font-black text-2xl">{s.val}</div>
                  <div className="text-white/50 text-xs mt-1">{t(s.labelKey)}</div>
                </div>
              ))}
            </div>
            <div className="flex flex-wrap gap-3">
              <button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 bg-green-500 hover:bg-green-400 text-white font-bold px-7 py-3.5 rounded-full transition-all duration-200 hover:-translate-y-0.5 cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-user-add-line"></i>
                <span>{t('activity.join')}</span>
              </button>
              <button
                onClick={() => document.getElementById('packages')?.scrollIntoView({ behavior: 'smooth' })}
                className="flex items-center gap-2 border border-white/20 text-white/70 hover:text-white hover:border-white/40 font-semibold px-7 py-3.5 rounded-full transition-all duration-200 cursor-pointer whitespace-nowrap text-sm"
              >
                <i className="ri-eye-line"></i>
                <span>{t('activity.view')}</span>
              </button>
            </div>
          </div>
        </div>
      </div>
      <style>{`
        @keyframes slideInLeft {
          from { opacity: 0; transform: translateX(-12px); }
          to { opacity: 1; transform: translateX(0); }
        }
      `}</style>
    </section>
  );
};

export default ActivityFeed;
